import { useCallback, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { HttpCodes } from '../enums/httpCodes';
import { HaygentChatState, HaygentMessage } from '../interfaces';
import { HaygentService } from '../services';

interface UseHaygentChatReturn {
  // State
  chatState: HaygentChatState;
  messages: HaygentMessage[];
  isLoading: boolean;
  error: string | null;

  // Actions
  openChat: () => Promise<void>;
  closeChat: () => void;
  toggleMaximize: () => void;
  sendMessage: (content: string) => Promise<void>;
  cancelRequest: () => void;
}

/**
 * Custom hook for managing Haygent AI chat state and interactions.
 *
 * Handles:
 * - Chat window state (collapsed/docked/maximized)
 * - Message history
 * - SSE streaming responses from the AI
 * - Session management
 * - Request cancellation
 */
export function useHaygentChat(): UseHaygentChatReturn {
  // Chat window display state: collapsed (FAB only), docked (small window), maximized (large overlay)
  const [chatState, setChatState] = useState<HaygentChatState>(HaygentChatState.Collapsed);
  // Full conversation history (both user and assistant messages)
  const [messages, setMessages] = useState<HaygentMessage[]>([]);
  // Loading state for API requests
  const [isLoading, setIsLoading] = useState(false);
  // User-facing error message
  const [error, setError] = useState<string | null>(null);

  // Persistent session ID across re-renders (not in state to avoid re-renders)
  const sessionIdRef = useRef<string | null>(null);
  // AbortController for cancelling ongoing SSE stream requests
  const abortControllerRef = useRef<AbortController | null>(null);

  /**
   * Opens the chat widget and creates a new session with the backend.
   */
  const openChat = useCallback(async () => {
    setError(null);
    setIsLoading(true);

    try {
      const session = await HaygentService.createSession();
      sessionIdRef.current = session.session_id;
      setMessages([]);
      setChatState(HaygentChatState.Docked);
    } catch (err) {
      const message = 'Failed to start chat session. Click the chat button to try again.';
      setError(message);
      toast.error(message);
      // eslint-disable-next-line no-console
      console.error('Failed to create session:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Closes the chat widget and resets the session.
   */
  const closeChat = useCallback(() => {
    sessionIdRef.current = null;
    setMessages([]);
    setError(null);
    setChatState(HaygentChatState.Collapsed);
  }, []);

  /**
   * Toggles between docked (small) and maximized (large) chat window states.
   */
  const toggleMaximize = useCallback(() => {
    setChatState((prev) => (prev === HaygentChatState.Maximized ? HaygentChatState.Docked : HaygentChatState.Maximized));
  }, []);

  /**
   * Sends a user message and streams the AI response.
   *
   * Flow:
   * 1. Add user message to history
   * 2. Add empty assistant message placeholder (for streaming)
   * 3. Stream response chunks and append to assistant message
   * 4. Handle errors with user-friendly messages
   */
  const sendMessage = useCallback(
    async (content: string) => {
      if (!sessionIdRef.current || !content.trim()) {
        return;
      }

      setError(null);
      setIsLoading(true);

      // Add user message
      const userMessage: HaygentMessage = { role: 'user', content: content.trim() };
      setMessages((prev) => [...prev, userMessage]);

      // Prepare messages for API (full history, excluding empty messages)
      const allMessages: HaygentMessage[] = [...messages, userMessage].filter((msg) => msg.content.trim().length > 0);

      // Add placeholder for assistant response
      setMessages((prev) => [...prev, { role: 'assistant', content: '' }]);

      // Create abort controller for request cancellation
      abortControllerRef.current = new AbortController();

      // Track if we received any content during streaming
      let receivedContent = '';

      try {
        // Stream chat response via SSE - onChunk callback fires for each received chunk
        await HaygentService.streamChat(
          sessionIdRef.current,
          allMessages,
          (chunk) => {
            // Append each chunk to the assistant's message as it arrives
            if (chunk.content) {
              receivedContent += chunk.content;
              // Update the last message (assistant placeholder) with streamed content
              setMessages((prev) => {
                const updated = [...prev];
                const lastIndex = updated.length - 1;
                if (updated[lastIndex]?.role === 'assistant') {
                  updated[lastIndex] = {
                    ...updated[lastIndex],
                    content: updated[lastIndex].content + chunk.content,
                  };
                }

                return updated;
              });
            }
          },
          abortControllerRef.current.signal,
        );

        // Check if stream completed without any content (empty response)
        if (!receivedContent.trim()) {
          setError('Received empty response. Please try again.');
        }
      } catch (err) {
        // Ignore abort errors - user cancelled the request intentionally
        if ((err as Error).name === 'AbortError') {
          return;
        }

        // Map HTTP status codes to user-friendly error messages
        const { status } = err as Error & { status?: number };
        let errorMessage = 'An error occurred. Please try again.';

        if (status === HttpCodes.UNAUTHORIZED) {
          errorMessage = 'Session expired. Please refresh the page.';
        } else if (status === HttpCodes.TOO_MANY_REQUESTS) {
          errorMessage = 'Too many requests. Please wait a moment.';
        } else if (status === HttpCodes.BAD_GATEWAY) {
          errorMessage = 'Haygent service is temporarily unavailable. Please try again later.';
        } else if (status === HttpCodes.SERVER_ERROR || status === HttpCodes.MAINTENANCE) {
          errorMessage = 'Server error. Please try again later.';
        }

        setError(errorMessage);
      } finally {
        // Clean up empty assistant placeholder if present
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (last?.role === 'assistant' && !last.content) {
            return prev.slice(0, -1);
          }

          return prev;
        });
        setIsLoading(false);
        abortControllerRef.current = null;
      }
    },
    [messages],
  );

  /**
   * Cancels any ongoing streaming request.
   * Useful when user wants to stop waiting for a response.
   */
  const cancelRequest = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsLoading(false);
    }
  }, []);

  return {
    chatState,
    messages,
    isLoading,
    error,
    openChat,
    closeChat,
    toggleMaximize,
    sendMessage,
    cancelRequest,
  };
}
